import { rulesContract } from "@openrift/shared/contracts/rules";
import type {
  RuleKind,
  RuleLanguage,
  RuleNumbersResponse,
  RulesPageResponse,
  RuleSourceResponse,
  RuleVersionsListResponse,
} from "@openrift/shared/types/api/rules";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import { rulesKeys } from "@/features/rules/lib/rules-query-keys";
import { serverCacheKeys } from "@/lib/query-keys";
import { serverCache } from "@/lib/server-cache";
import { apiOrpcClient } from "@/lib/server-fns/orpc-client";

interface RulesDocumentInput {
  kind: RuleKind;
  language: RuleLanguage;
  version: string;
}

const fetchRulesAtVersion = createServerFn({ method: "GET" })
  .validator((input: RulesDocumentInput) => input)
  .handler(({ data }): Promise<RulesPageResponse> =>
    serverCache.query({
      queryKey: serverCacheKeys.rules.page(data.kind, data.language, data.version),
      queryFn: () => apiOrpcClient(rulesContract).page(data),
    }),
  );

const fetchRulesSource = createServerFn({ method: "GET" })
  .validator((input: RulesDocumentInput) => input)
  .handler(({ data }): Promise<RuleSourceResponse> =>
    serverCache.query({
      queryKey: serverCacheKeys.rules.source(data.kind, data.language, data.version),
      queryFn: () => apiOrpcClient(rulesContract).source(data),
    }),
  );

const fetchRuleNumbers = createServerFn({ method: "GET" })
  .validator((input: { kind: RuleKind; version: string }) => input)
  .handler(({ data }): Promise<RuleNumbersResponse> =>
    serverCache.query({
      queryKey: serverCacheKeys.rules.numbers(data.kind, data.version),
      queryFn: () => apiOrpcClient(rulesContract).numbers(data),
    }),
  );

const fetchVersions = createServerFn({ method: "GET" })
  .validator((input: { kind?: RuleKind; language: RuleLanguage }) => input)
  .handler(({ data }): Promise<RuleVersionsListResponse> =>
    serverCache.query({
      queryKey: serverCacheKeys.rulesVersions.list(data.language, data.kind ?? "all"),
      queryFn: () => apiOrpcClient(rulesContract).versions(data),
    }),
  );

export function rulesAtVersionQueryOptions(
  kind: RuleKind,
  language: RuleLanguage,
  version: string,
) {
  return queryOptions({
    queryKey: rulesKeys.byVersion(kind, language, version),
    queryFn: () => fetchRulesAtVersion({ data: { kind, language, version } }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function rulesSourceQueryOptions(kind: RuleKind, language: RuleLanguage, version: string) {
  return queryOptions({
    queryKey: rulesKeys.source(kind, language, version),
    queryFn: () => fetchRulesSource({ data: { kind, language, version } }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function ruleNumbersQueryOptions(kind: RuleKind, version: string) {
  return queryOptions({
    queryKey: rulesKeys.numbers(kind, version),
    queryFn: () => fetchRuleNumbers({ data: { kind, version } }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    select: (data) => new Set(data.numbers) as ReadonlySet<string>,
  });
}

export function ruleVersionsQueryOptions(kind: RuleKind, language: RuleLanguage = "en") {
  return queryOptions({
    queryKey: rulesKeys.versions(kind, language),
    queryFn: () => fetchVersions({ data: { kind, language } }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
